import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2Icon, ShieldCheckIcon } from 'lucide-react';
import { FramePlaceholderBoundary } from './FramePlaceholderBoundary';
import { FormField } from '../ui/FormField';
import { buttonClasses } from '../../utils/buttonStyles';

/**
 * VISUAL PLACEHOLDER — native Website Forge FEEDBACK (contact) frame with CAPTCHA.
 *
 * Validation and "success" are SIMULATED in the browser; nothing is transmitted or
 * emailed. Field ids here (name/email/message) are prototype names only. At integration,
 * render the EXISTING native feedback frame, preserving its field names/IDs, hidden
 * inputs, CAPTCHA, server validation and its purpose-matched thank-you redirect
 * (mirrored by /contact/thank-you).
 */
type FieldKey = 'name' | 'email' | 'message';
type Values = Record<FieldKey, string>;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
function validate(values: Values): Partial<Record<FieldKey, string>> {
  const errors: Partial<Record<FieldKey, string>> = {};
  if (!values.name.trim()) errors.name = 'Please enter your name.';
  if (!values.email.trim()) errors.email = 'Please enter your email address.';else if (!EMAIL_PATTERN.test(values.email.trim())) errors.email = 'Please enter a valid email address, like name@example.com.';
  if (!values.message.trim()) errors.message = 'Please enter a message.';
  return errors;
}
export function WfFeedbackFramePreview() {
  const [values, setValues] = useState<Values>({
    name: '',
    email: '',
    message: ''
  });
  const [errors, setErrors] = useState<Partial<Record<FieldKey, string>>>({});
  const [sending, setSending] = useState(false);
  const fieldRefs = useRef<Partial<Record<FieldKey, HTMLInputElement | HTMLTextAreaElement | null>>>({});
  const navigate = useNavigate();
  const update = (key: FieldKey) => (value: string) => {
    setValues(v => ({
      ...v,
      [key]: value
    }));
    if (errors[key]) setErrors(e => ({
      ...e,
      [key]: undefined
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const next = validate(values);
    setErrors(next);
    const first = (['name', 'email', 'message'] as FieldKey[]).find(k => next[k]);
    if (first) {
      fieldRefs.current[first]?.focus();
      return;
    }
    setSending(true);
    // Simulated success only — no request is made.
    window.setTimeout(() => navigate('/contact/thank-you', {
      state: {
        name: values.name.trim()
      }
    }), 700);
  };
  const errorCount = Object.values(errors).filter(Boolean).length;
  return <FramePlaceholderBoundary frame="WfFeedbackFramePreview" data-id="element-90" data-mp="2vbnrkqyzw3hp">
      <form noValidate onSubmit={handleSubmit} aria-labelledby="contact-form-title" className="space-y-6" data-id="element-91" data-mp="3943m13dtyt70">
        <h2 id="contact-form-title" className="font-display text-3xl text-ink" data-id="element-92" data-mp="354urvxevotax">
          Send A Message
        </h2>
        {errorCount > 0 && <p role="alert" className="border-l-2 border-danger pl-4 text-[15px] text-danger" data-id="element-93" data-mp="3e49kb2elvdy2">
            Please fix {errorCount === 1 ? 'the highlighted field' : `the ${errorCount} highlighted fields`} below.
          </p>}
        <div className="grid gap-6 sm:grid-cols-2" data-id="element-94" data-mp="6k041of2lpdl">
          <FormField id="name" label="Name" value={values.name} onChange={update('name')} error={errors.name} autoComplete="name" inputRef={el => fieldRefs.current.name = el} data-id="element-95" data-mp="6he16234jy1p" />
          <FormField id="email" label="Email" type="email" value={values.email} onChange={update('email')} error={errors.email} autoComplete="email" inputRef={el => fieldRefs.current.email = el} data-id="element-96" data-mp="3exyyglme92oz" />
        </div>
        <FormField id="message" label="Message" multiline value={values.message} onChange={update('message')} error={errors.message} inputRef={el => fieldRefs.current.message = el} data-id="element-97" data-mp="39y4aapu06r7m" />

        {/* CAPTCHA placeholder — the native feedback frame supplies the real verification. */}
        <div aria-hidden="true" className="flex min-h-[72px] items-center gap-3 rounded-[3px] border border-dashed border-line-strong bg-surface px-5 text-[14px] text-ink-muted" data-id="element-98" data-mp="2o1b1r3lhsl76">
          <ShieldCheckIcon className="h-5 w-5 text-bronze-deep" strokeWidth={1.5} data-id="element-99" data-mp="2e9nfri7pfwd3" />
          Verification
        </div>

        <button type="submit" disabled={sending} className={`${buttonClasses('primary')} w-full sm:w-auto`} data-id="element-100" data-mp="2a6iflmb3k6rm">
          {sending ? <>
              <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-101" data-mp="209m6beddjgm0" />
              Sending…
            </> : 'Send message'}
        </button>
      </form>
    </FramePlaceholderBoundary>;
}